import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router-dom';
import { useAuth } from './stores/auth';
import { DashboardLayout } from './pages/dashboard/DashboardLayout';
import { MySitesPage } from './pages/dashboard/MySitesPage';
import { CreateFromTemplatePage } from './pages/dashboard/CreateFromTemplatePage';
import { HomePage } from './pages/marketing/HomePage';
import { PricingPage } from './pages/marketing/PricingPage';
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { VerifyPage } from './pages/auth/VerifyPage';
import EditorPage from './editor/EditorPage';
import { AccountSettingsPage } from './pages/account/AccountSettingsPage';
import { BillingPage } from './pages/account/BillingPage';
import { SiteSettingsPage } from './pages/account/SiteSettingsPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminSitesPage } from './pages/admin/AdminSitesPage';
import { AdminRequestsPage } from './pages/admin/AdminRequestsPage';
import { TemplateGalleryPage } from './pages/templates/TemplateGalleryPage';
import { TemplatePreviewPage } from './pages/templates/TemplatePreviewPage';
import { GettingStartedPage } from './pages/help/GettingStartedPage';
import { TermsPage } from './pages/legal/TermsPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { AbusePage } from './pages/legal/AbusePage';

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="sarnia-container" style={{ paddingTop: '3rem' }}>Loading…</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Outlet />;
}

function NotFound() {
  return (
    <div className="sarnia-container" style={{ paddingTop: '3rem', textAlign: 'center' }}>
      <h1>Page not found</h1>
      <p>The page you were looking for does not exist.</p>
      <a className="sarnia-btn sarnia-btn--primary" href="/">Back to home</a>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <HomePage />
  },
  {
    path: '/pricing',
    element: <PricingPage />
  },
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/signup',
    element: <SignupPage />
  },
  {
    path: '/verify-email',
    element: <VerifyPage />
  },
  { path: '/templates', element: <TemplateGalleryPage /> },
  { path: '/templates/:templateId', element: <TemplatePreviewPage /> },
  { path: '/help/getting-started', element: <GettingStartedPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/abuse', element: <AbusePage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        path: '/dashboard',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/sites" replace />
          },
          {
            path: 'sites',
            element: <MySitesPage />
          },
          {
            path: 'sites/new',
            element: <CreateFromTemplatePage />
          },
          {
            path: 'sites/:siteId/settings',
            element: <SiteSettingsPage />
          },
          {
            path: 'account',
            element: <AccountSettingsPage />
          },
          {
            path: 'account/billing',
            element: <BillingPage />
          }
        ]
      },
      {
        path: '/editor/:siteId',
        element: <EditorPage />
      },
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'sites', element: <AdminSitesPage /> },
          { path: 'requests', element: <AdminRequestsPage /> }
        ]
      }
    ]
  },
  {
    path: '*',
    element: <NotFound />
  }
]);
